import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/shared/Header";
import { DateCard } from "../components/shared/DateCard";
import { SlotCard } from "../components/shared/SlotCard";
import { SlotModal } from "../components/shared/SlotModal";
import { Footer } from "../components/shared/Footer";
import { useBookingStore } from "../store/bookingStore";
import { apiEndpoints } from "../services/api";
import { BookingDate } from "../types";
import { Calendar, Clock } from "lucide-react";

export const CalendarPage: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const {
    dates,
    selectedDate,
    slots,
    loadingDates,
    loadingSlots,
    setSession,
    setDates,
    setSelectedDate,
    setSlots,
    setSelectedSlot,
    setSelectedConsultant,
    setLoadingDates,
    setLoadingSlots,
    resetSlotSelection,
  } = useBookingStore();

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load dates on component mount
  useEffect(() => {
    const loadDates = async () => {
      try {
        setLoadingDates(true);
        const response = await apiEndpoints.getDates();
        setDates(response.data.data);
        // Only auto-select first date on desktop
        if (!isMobile) {
          setSelectedDate(response.data.data[0]);
        }
      } catch (error) {
        console.error("Error loading dates:", error);
      } finally {
        setLoadingDates(false);
      }
    };

    loadDates();
  }, [setDates, setLoadingDates, setSelectedDate, isMobile]);

  // Load slots when date is selected
  useEffect(() => {
    if (selectedDate) {
      const loadSlots = async () => {
        try {
          setLoadingSlots(true);
          resetSlotSelection();
          const response = await apiEndpoints.getSlots(selectedDate._id);
          setSlots(response.data.data);
        } catch (error) {
          console.error("Error loading slots:", error);
        } finally {
          setLoadingSlots(false);
        }
      };

      loadSlots();
    }
  }, [selectedDate, setSlots, setLoadingSlots, resetSlotSelection]);

  const handleDateSelect = (date: BookingDate) => {
    setSelectedDate(date);
    if (isMobile) {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSlotSelect = async (slotConsultantMappingId: string) => {
    try {
      const response = await apiEndpoints.startSession(slotConsultantMappingId);
      console.log("response startSession", response);
      const sessionData = response.data.data;

      // Find the selected slot and consultant
      const selectedSlot = slots.find((slot) =>
        slot.consultants.some(
          (consultant) =>
            consultant.slotConsultantMappingId === slotConsultantMappingId
        )
      );

      const selectedConsultant = selectedSlot?.consultants.find(
        (consultant) =>
          consultant.slotConsultantMappingId === slotConsultantMappingId
      );

      console.log("selectedSlot", selectedSlot);
      console.log("selectedConsultant", selectedConsultant);
      console.log("sessionData", sessionData);
      if (selectedSlot && selectedConsultant && sessionData) {
        try {
          setSelectedSlot(selectedSlot);
          setSelectedConsultant(selectedConsultant);
          setSession(sessionData);
          // Close modal on mobile
          if (isMobile) {
            setIsModalOpen(false);
          }
          // Navigate to booking form using React Router
          navigate("/booking");
        } catch (error) {
          console.error("Error setting selected slot and consultant:", error);
        }
      }
    } catch (error) {
      console.error("Error starting session:", error);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <Header
        title="Book Your Consultation"
        subtitle="Select a date and time that works for you"
      />

      <div className="container mx-auto px-6 py-12 max-w-6xl flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calendar Section */}
          <div>
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                Book Your Consultation
              </h2>
              <p className="text-gray-600">
                Select a date and time that works for you
              </p>
            </div>

            {loadingDates ? (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-7 gap-2 sm:gap-3 md:gap-4">
                {[...Array(15)].map((_, index) => (
                  <div key={index} className="animate-pulse">
                    <div className="rounded-lg border-2 p-2 sm:p-3 bg-gray-50">
                      <div className="h-2 sm:h-3 bg-gray-100 rounded mb-1 sm:mb-2"></div>
                      <div className="h-4 sm:h-6 bg-gray-100 rounded mb-1 sm:mb-2"></div>
                      <div className="h-2 sm:h-3 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                {dates.map((date) => (
                  <DateCard
                    key={date._id}
                    date={date}
                    isSelected={selectedDate?._id === date._id}
                    onClick={handleDateSelect}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Slots Section - Desktop Only */}
          {!isMobile && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  Choose a time
                </h2>
                <p className="text-gray-600">
                  {selectedDate
                    ? `Available slots for ${
                        selectedDate.date
                          ? new Date(selectedDate.date).toLocaleDateString(
                              "en-US",
                              { weekday: "long", month: "long", day: "numeric" }
                            )
                          : ""
                      }`
                    : "Select a date to view available time slots"}
                </p>
              </div>

              {!selectedDate ? (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">
                    Please select a date to view available time slots
                  </p>
                </div>
              ) : loadingSlots ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="rounded-lg border-2 p-4 bg-gray-50">
                        <div className="flex justify-between mb-3">
                          <div className="h-5 bg-gray-100 rounded w-16"></div>
                          <div className="h-5 bg-gray-100 rounded w-12"></div>
                        </div>
                        <div className="h-4 bg-gray-100 rounded mb-3 w-24"></div>
                        <div className="h-10 bg-gray-100 rounded"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : slots.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No available slots for this date</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                  {slots.map((slot) => (
                    <SlotCard
                      key={slot._id}
                      slot={slot}
                      onSlotSelect={handleSlotSelect}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Slot Modal for Mobile */}
      <SlotModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        selectedDate={selectedDate}
        slots={slots}
        loadingSlots={loadingSlots}
        onSlotSelect={handleSlotSelect}
      />
      
      <Footer />
    </div>
  );
};
